'use client'
import type { ColumnDef } from '@tanstack/react-table'

import { safeParseNumber } from '@morpho-org/blue-sdk-viem'
import { InterfaceChainUtils } from '@morpho-org/dapp-core'
import { CellTokenPrice, NetworkAvatar as Network } from '@morpho-org/ui-v6/client'
import { formatters } from '@morpho-org/ui-v6/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import {
  columnFilteringFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createSortedRowModel,
  rowSortingFeature,
  sortFns,
  tableFeatures
} from '@tanstack/react-table'

import CellNetApy from '@/components/common/Cells/CellNetApy'
import { HealthFactorCell } from '@/components/common/Cells/HealthFactorCell'
import { MarketPositionHistoryHoverCard } from '@/components/common/MarketPositionHistoryHoverCard'
import MarketPositionManagerClient from '@/components/common/PositionManager/MarketPositionManager/MarketPositionManager.client'
import { WarningIcon } from '@/components/common/WarningIcon'
import { OwnPortfolioOnly } from '@/components/DashboardPage/OwnPortfolioOnly'
import { AVG_APY_DURATION_LABEL } from '@/constants/avgApy'
import { getParsedValue } from '@/helpers/formatters'

import type { BorrowPositionsTableRow } from './BorrowPositionsTable.types'

const percentageFormatter = formatters.percent.default('-').digits(2).unit('%')

export const columnLabelsBorrowPositions = {
  network: 'Network',
  collateral: 'Collateral',
  borrow: 'Loan',
  rate: 'Rate',
  health: 'Health',
  utilization: 'Utilization'
}

export const borrowPositionsTableFeatures = tableFeatures({
  columnFilteringFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  sortFns,
  sortedRowModel: createSortedRowModel()
})

export type BorrowPositionsTableFeatures = typeof borrowPositionsTableFeatures

export const columnsBorrowPositions: ColumnDef<
  BorrowPositionsTableFeatures,
  BorrowPositionsTableRow
>[] = [
  {
    id: 'network',
    minSize: 100,
    size: 104,
    accessorFn: row => InterfaceChainUtils.getMetadata(row.market.chainId).chainIdentifier,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {columnLabelsBorrowPositions.network}
          </span>
        </TooltipTrigger>
        <TooltipContent>The network of the market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const chainId = row.original.market.chainId
      const chainName = InterfaceChainUtils.getMetadata(chainId).chain.name
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center">
              <Network
                chainId={chainId}
                srcAvatarProps={{
                  size: 'small'
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>{chainName}</TooltipContent>
        </Tooltip>
      )
    }
  },
  {
    id: 'borrow',
    size: 180,
    accessorFn: row => row.borrow.value,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '14px' }}>
            {columnLabelsBorrowPositions.borrow}
          </span>
        </TooltipTrigger>
        <TooltipContent>The total amount you have borrowed for this position.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const isPositionHistoryEnabled = row.original.borrow.isPositionHistoryEnabled
      const isHoverable = isPositionHistoryEnabled && !row.original.isNew

      return (
        <div className="flex flex-row items-center">
          <CellTokenPrice
            data-testid={`borrow-position-loan-${row.original.market.marketId}`}
            token={row.original.borrow.token}
            amount={row.original.borrow.amount}
            value={getParsedValue(row.original.borrow.value, safeParseNumber)}
            isHoverCardDisabled={!isHoverable}
            withAvatar
            content={
              isPositionHistoryEnabled ? (
                <MarketPositionHistoryHoverCard
                  mode="borrowAssetsUsd"
                  address={row.original.borrow.userAddress}
                  chainId={row.original.borrow.token.chain.id}
                  marketId={row.original.market.marketId}
                  assetsUsd={getParsedValue(row.original.borrow.value, safeParseNumber)}
                />
              ) : null
            }
          />
        </div>
      )
    }
  },
  {
    id: 'collateral',
    size: 180,
    accessorFn: row => row.collateral.value,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '14px' }}>
            {columnLabelsBorrowPositions.collateral}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The total amount of collateral you have deposited for this position.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      if (!row.original.collateral.token) return null

      const isPositionHistoryEnabled = row.original.borrow.isPositionHistoryEnabled
      const isHoverable = isPositionHistoryEnabled && !row.original.isNew

      return (
        <div className="flex flex-row items-center">
          <CellTokenPrice
            data-testid={`borrow-position-collateral-${row.original.market.marketId}`}
            token={row.original.collateral.token}
            amount={row.original.collateral.amount}
            value={getParsedValue(row.original.collateral.value, safeParseNumber)}
            netApy={row.original.collateral.netApy}
            isHoverCardDisabled={!isHoverable}
            withAvatar
            renderWarnings={<WarningIcon warnings={row.original.collateral.warnings} />}
            content={
              isPositionHistoryEnabled ? (
                <MarketPositionHistoryHoverCard
                  mode="collateralUsd"
                  address={row.original.borrow.userAddress}
                  chainId={row.original.borrow.token.chain.id}
                  marketId={row.original.market.marketId}
                  assetsUsd={getParsedValue(row.original.collateral.value, safeParseNumber)}
                />
              ) : null
            }
          />
        </div>
      )
    }
  },
  {
    id: 'netApy',
    // Fits the CellNetApy hover pill (value + up to 2 reward/points icons +
    // padding + cell indent) so it can't overflow into the next cell and get
    // clipped by its hover background.
    size: 125,
    accessorFn: row => row.netApy.avgNetBorrowApy,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '14px' }}>
            {columnLabelsBorrowPositions.rate}
          </span>
        </TooltipTrigger>
        <TooltipContent>{`Average borrow rate for the past ${AVG_APY_DURATION_LABEL}.`}</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellNetApy
        variant="market"
        performanceFee={0n}
        netApy={row.original.netApy.avgNetBorrowApy}
        apy={row.original.netApy.avgBorrowApy}
        rewards={row.original.netApy.rewards}
        points={row.original.netApy.points}
        asset={row.original.collateral.token}
        merklContext={{
          kind: 'market',
          chainId: row.original.borrow.token.chain.id,
          marketId: row.original.market.marketId,
          loanTokenAddress: row.original.borrow.token.address
        }}
      />
    )
  },
  {
    id: 'health',
    size: 100,
    accessorFn: row => row.health.healthFactor,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '14px' }}>
            {columnLabelsBorrowPositions.health}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Position&apos;s loan-to-value ratio (LTV) compared to its liquidation threshold (LLTV).
          Liquidation occurs if health factor reaches 1.
        </TooltipContent>
      </Tooltip>
    ),
    enableSorting: true,
    cell: ({ row }) => (
      <HealthFactorCell data={row.original} chainId={row.original.market.chainId} />
    )
  },
  {
    id: 'utilization',
    size: 80,
    accessorFn: row => row.market.utilization,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {columnLabelsBorrowPositions.utilization}
          </span>
        </TooltipTrigger>
        <TooltipContent>Share of supplied liquidity that’s currently borrowed.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      return (
        <span className="text-foreground text-xs">
          {percentageFormatter.of(row.original.market.utilization)}
        </span>
      )
    }
  },
  {
    id: 'interest',
    size: 180,
    accessorFn: row => row.borrow.accruedInterest.usdValue,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '14px' }}>
            Accrued Interest
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Interest accrued to date on your active borrow positions, excluding rewards.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      return (
        <CellTokenPrice
          token={row.original.borrow.token}
          amount={row.original.borrow.accruedInterest.assetValue}
          value={row.original.borrow.accruedInterest.usdValue}
          withAvatar={true}
        />
      )
    }
  },
  {
    id: 'positionManager',
    size: 56,
    header: () => null,
    cell: ({ row }) => (
      <OwnPortfolioOnly>
        <MarketPositionManagerClient
          marketId={row.original.market.marketId}
          loanAsset={row.original.borrow.token}
          collateralAsset={row.original.collateral.token}
          chainId={row.original.borrow.token.chain.id}
          actionGroup="borrow"
        />
      </OwnPortfolioOnly>
    )
  }
]
